/**
 * DashboardLayout — sidebar Loveable identique au source, alimentée par
 * une liste d'items dépendante du rôle (client / admin / super_admin /
 * technicien). Chaque item est une vraie route.
 */
import type { Permission, UserRole } from '@bcl/shared';
import { hasPermission } from '@bcl/shared';
import {
  BarChart3,
  CalendarClock,
  ClipboardList,
  CreditCard,
  FileCheck2,
  FileText,
  HelpCircle,
  History,
  Inbox,
  Menu,
  MessageSquare,
  Package,
  ShieldCheck,
  Upload,
  UserCog,
  UsersRound,
  Wrench,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/site/brand';
import { ImpersonationBanner } from '@/components/impersonation-banner';
import { NotificationBell } from '@/components/notification-bell';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

type SidebarItem = {
  name: string;
  href: string;
  icon: typeof BarChart3;
  /** Permission requise pour voir l'entrée (back-office admin uniquement). */
  permission?: Permission;
  /** Entrée réservée au super_admin (ex : gestion globale des comptes). */
  superAdminOnly?: boolean;
};

const clientItems: SidebarItem[] = [
  { name: 'Tableau de bord', href: '/dashboard', icon: BarChart3 },
  { name: 'Mes documents', href: '/dashboard/documents', icon: FileText },
  { name: 'Mes offres', href: '/dashboard/offres', icon: Package },
  { name: 'Mes demandes', href: '/dashboard/requests', icon: Upload },
  { name: 'Mes rendez-vous', href: '/dashboard/rendez-vous', icon: CalendarClock },
  { name: 'Mes factures', href: '/dashboard/factures', icon: CreditCard },
  { name: 'Messagerie', href: '/dashboard/messages', icon: MessageSquare },
  { name: 'Assistance', href: '/dashboard/assistance', icon: HelpCircle },
];

// Back-office centré dossier : le travail se fait dans la fiche dossier
// (documents, rendez-vous, technicien, paiement). Les comptes (clients,
// partenaires) sont gérés dans « Utilisateurs ». On garde donc une sidebar
// resserrée : Dossiers est le hub, puis l'écosystème partenaire, puis les
// outils super_admin.
const adminItems: SidebarItem[] = [
  { name: 'Tableau de bord', href: '/admin', icon: BarChart3 },
  { name: 'Utilisateurs', href: '/admin/users', icon: UsersRound, superAdminOnly: true },
  { name: 'Dossiers', href: '/admin/projects', icon: ClipboardList, permission: 'projects.view' },
  // Lead → dossier → intervention → planning : le flux d'exécution, regroupé.
  { name: 'Leads', href: '/admin/leads', icon: Inbox, permission: 'partners.manage' },
  { name: 'Interventions', href: '/admin/interventions', icon: Wrench, permission: 'planning.manage' },
  { name: 'Planning', href: '/admin/planning', icon: CalendarClock, permission: 'planning.manage' },
  // --- Gestion ---
  { name: 'Paiements', href: '/admin/payments', icon: CreditCard, permission: 'payments.view' },
  { name: 'Packs & tarifs', href: '/admin/packs', icon: Package, permission: 'packs.manage' },
  { name: 'Factures partenaires', href: '/admin/partner-invoices', icon: CreditCard, permission: 'partners.manage' },
  // --- Outils super_admin ---
  { name: 'Équipe BCL', href: '/admin/staff', icon: UserCog, superAdminOnly: true },
  { name: 'Journal', href: '/admin/audit', icon: History, superAdminOnly: true },
  { name: 'Assistance', href: '/admin/assistance', icon: HelpCircle },
];

const partnerItems: SidebarItem[] = [
  { name: 'Mon profil', href: '/partenaire', icon: ShieldCheck },
  { name: 'Mes leads', href: '/partenaire/leads', icon: Inbox },
  { name: 'Mes interventions', href: '/partenaire/interventions', icon: Wrench },
  { name: 'Mes rendez-vous', href: '/partenaire/rendez-vous', icon: CalendarClock },
  { name: 'Assistance', href: '/partenaire/assistance', icon: HelpCircle },
];

const technicianItems: SidebarItem[] = [
  { name: 'Tableau de bord', href: '/technicien', icon: BarChart3 },
  // Le dossier est le point d'entrée : tout (accepter/refuser le RDV, clôturer,
  // compte rendu) se fait depuis « Mes dossiers ». « Mon planning » = calendrier.
  { name: 'Mes dossiers', href: '/technicien/projects', icon: ClipboardList },
  { name: 'Mon planning', href: '/technicien/planning', icon: CalendarClock },
  { name: 'Comptes rendus', href: '/technicien/reports', icon: FileText },
  { name: 'Documents', href: '/technicien/documents', icon: FileCheck2 },
  { name: 'Assistance', href: '/technicien/assistance', icon: HelpCircle },
];

function itemsForRole(role: UserRole | undefined): SidebarItem[] {
  if (role === 'admin' || role === 'super_admin') return adminItems;
  if (role === 'technicien') return technicianItems;
  if (role === 'partenaire') return partnerItems;
  return clientItems;
}

type Variant = 'client' | 'admin' | 'technicien' | 'partenaire';

function variantTitle(variant: Variant): string {
  switch (variant) {
    case 'admin':
      return 'Back-office BCL';
    case 'technicien':
      return 'Espace technicien';
    case 'partenaire':
      return 'Espace partenaire';
    default:
      return 'Tableau de bord';
  }
}

export function DashboardLayout({
  children,
  title,
  variant = 'client',
}: {
  children: ReactNode;
  title?: string;
  variant?: Variant;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Masque les entrées réservées au super_admin, puis celles dont l'admin n'a
  // pas la permission. Seules les entrées admin/super_admin portent une
  // permission : `user` est alors forcément défini.
  const items = itemsForRole(user?.role).filter((item) => {
    if (item.superAdminOnly && user?.role !== 'super_admin') return false;
    if (!item.permission) return true;
    return hasPermission(user!.role, user!.permissions, item.permission);
  });
  const headerTitle = title ?? variantTitle(variant);

  function handleLogout() {
    signOut();
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-bcl-grey">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transition-transform duration-300 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <Link to="/">
              <BrandLogo size="sm" />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-gray-500 rounded-md hover:bg-gray-100 lg:hidden"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-6 border-b">
            <p className="font-medium">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            {user?.role && user.role !== 'client' && (
              <p className="mt-1 text-xs uppercase tracking-wide text-bcl-blue">
                {user.role.replace('_', ' ')}
              </p>
            )}
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {items.map((item) => {
              const isActive = isItemActive(location.pathname, item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center px-3 py-2 rounded-md text-sm font-medium',
                    isActive
                      ? 'bg-bcl-blue text-white'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t">
            <Button
              variant="outline"
              className="w-full justify-start text-gray-700"
              onClick={handleLogout}
            >
              Se déconnecter
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64">
        <ImpersonationBanner />
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-gray-500 rounded-md hover:bg-gray-100 lg:hidden"
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold">{headerTitle}</h1>
            </div>
            <NotificationBell />
          </div>
        </header>

        <main className="p-3 sm:p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}

/**
 * Une route est "active" si elle correspond exactement à l'URL, OU
 * si l'URL démarre par le préfixe non-racine. Évite que "/admin"
 * match aussi "/admin/clients", "/admin/projects" etc.
 */
function isItemActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  if (href === '/dashboard' || href === '/admin' || href === '/technicien') {
    return false;
  }
  return pathname.startsWith(`${href}/`) || pathname === href;
}
